import React, { useContext, useState, useMemo } from "react";
import axios from "axios";
import ReactModal from "react-modal";
import { useHistory } from "react-router-dom";

import { SettingsContext } from "../../../contexts";
import { Visible } from "../../../core/components";
import { Loading, Money, Select } from "../../../components";
import SendPaymentLinkModal from "./SendPaymentLinkModal";

const getActions = () => {
  return [
    { id: "send_payment_link", name: "Send payment link" },
    { id: "created", name: "Created" },
    { id: "paid", name: "Mark as paid" },
    { id: "cancelled", name: "Canceled" },
    { id: "refunded", name: "Refunded" },
    { id: "add_tracking", name: "Add Tracking No" },
    { id: "edit_px", name: "Edit Part Exchange" },
  ];
};

const AdminActions = ({ order, item, open, last, setNewStatus }) => {
  const history = useHistory();
  const settings = useContext(SettingsContext);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [action, setAction] = useState(null);
  const [trackingNo, setTrackingNo] = useState("");
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  // Bumped each time the payment-link modal closes, used as the Select
  // component's key so its internal state resets and the same option
  // ("send_payment_link") can be re-selected to reopen the modal.
  const [selectResetKey, setSelectResetKey] = useState(0);
  const actionOptions = useMemo(() => getActions(), []);

  const updateOrder = async () => {
    if (!action || (action === "add_tracking" && !trackingNo)) {
      return;
    }

    try {
      setSubmitting(true);
      setError(false);

      const { data } = await axios.put(`/api/orders/${order.id}`, {
        status: action === "add_tracking" ? undefined : action,
        tracking_number: trackingNo || undefined,
      });

      setNewStatus(data.status);

      setAction(null);
      setTrackingNo("");
      setSubmitting(false);
    } catch (err) {
      setError(true);
      setAction(null);
      setTrackingNo("");
      setSubmitting(false);
    }
  };

  const doAction = () => {
    if (action === "edit_px") {
      history.push(
        `/request/px-order/${order?.px_request_id || ""}?${
          settings.iframeQueryParams
        }`,
        { order, listing: item?.listing }
      );
    } else if (action === "send_payment_link") {
      setPaymentLinkModalOpen(true);
    } else {
      updateOrder();
    }
  };

  const closePaymentLinkModal = () => {
    setPaymentLinkModalOpen(false);
    setAction(null);
    // Force the action Select to remount so its internal state forgets the
    // previous "send_payment_link" selection — otherwise selecting it again
    // is a no-op and the arrow never re-appears.
    setSelectResetKey((k) => k + 1);
  };

  const topBorderClass = order.items.length > 1 && open ? "top-border" : "";

  return (
    <div className={`col-last item-col ${topBorderClass}`}>
      <div className="manage-order-item">
        {last ? (
          <>
            <div className="main">
              <Select
                {...{
                  className: `select-container mb-3 order-action-selector ${action &&
                    "loaded"}`,
                  placeholder: (
                    <>
                      <Visible when={submitting}>
                        <Loading />
                      </Visible>

                      {!submitting && (action || "Action")}
                    </>
                  ),
                  options: actionOptions,
                  onSelectionChange: (a) => setAction(a),
                  field: { name: "action_selector", value: action },
                  form: { setFieldValue: () => {} },
                }}
                key={`action-select-${selectResetKey}`}
              />

              <Visible when={!!action}>
                <div className="do-action" onClick={() => doAction()}>
                  <img src="/assets/icons/next-2.svg"></img>
                </div>
              </Visible>
            </div>
            <div>
              <span className="amount">
                <Money amount={open ? item.price : order.total_amount} currency={order.currency} />
              </span>
            </div>
          </>
        ) : (
          <div>
            <span className="amount">
              <Money amount={item.price} currency={order.currency} />
            </span>
          </div>
        )}

        <Visible when={error}>
          <div className="error">Operation failed. Please try again.</div>
        </Visible>
      </div>

      <ReactModal
        isOpen={action === "add_tracking"}
        onRequestClose={() => setAction(null)}
        style={{
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            translate: "-50% -50%",
            background: "#fff",
            WebkitOverflowScrolling: "touch",
            borderRadius: "4px",
            outline: "none",
            padding: "20px",
            minWidth: "300px",
            height: "fit-content",
          },
        }}
      >
        <div className="tracking-container">
          <h3>Add Tracking Number</h3>

          <div className="input-container">
            <input
              placeholder="Tracking Number"
              type="text"
              name="tracking_number"
              onChange={(e) => setTrackingNo(e.target.value)}
              value={trackingNo}
            />
          </div>
          <div className="buttons-container">
            <button
              onClick={() => {
                setTrackingNo("");
                setAction(null);
              }}
            >
              Cancel
            </button>

            <button onClick={() => trackingNo && updateOrder()}>
              {submitting ? <Loading /> : "Run Action"}
            </button>
          </div>
        </div>
      </ReactModal>

      <SendPaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={closePaymentLinkModal}
        order={order}
      />
    </div>
  );
};

export default AdminActions;
